// CHAT-SWITCH-A (docs/direction/2026-10-28-chat-slack-switch.md wave 1), the composer side:
//   - drafts: what you typed survives leaving the channel, topic, thread or DM and a reload. The
//     audit asks for "survive navigation and reload", not other devices, so they live in this
//     browser (localStorage, every access guarded: a private window simply keeps none);
//   - `@channel` / `@here` in the @ menu of a channel (never a DM); the server checks the
//     channel's policy again and says why it refuses;
//   - reminders: "Remind me about this" on a message (20 minutes, 1 hour, tomorrow, custom) and
//     `/remind` (the menu opens the dialog; a typed `/remind in 20m call the bank` is set at
//     once), listed under Saved with a Cancel.
import type { OperationOutput } from "@companyos/sdk";
import {
  Button,
  type ComposerProps,
  type ComposerValue,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  EmptyState,
  Field,
  FieldLabel,
  InfoTip,
  Input,
  type MentionItem,
  type SlashCommand,
  toast,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { AlarmClockIcon, BellRingIcon, XIcon } from "lucide-react";
import { type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { keepFocus } from "../../focus/focus";
import { problemText } from "../ops-parts";
import { useCanManage } from "./channel-settings";
import { BROADCAST_REF } from "./chat-optimistic";
import { type Channel, chatKeys, type MessageView } from "./chat-queries";
import type { ChatContext } from "./use-chat";

type ChatReminder = OperationOutput<"chatReminders.list">["data"][number];

// Drafts -----------------------------------------------------------------------------------------

const DRAFT_PREFIX = "cos:chat-draft:v1";
/** A draft untouched this long is dropped when next read. */
const DRAFT_TTL_MS = 30 * 86_400_000;

/**
 * CHAT-SWITCH-B: a draft brought back from a send the server may already have (`client_id`,
 * and the signature of the request it went with, `requestSig`): sent again as the same request
 * it keeps that idempotency key, so the server answers the first message instead of posting a
 * second.
 */
type StoredDraft = {
  blocks: unknown[];
  at: number;
  client_id?: string;
  client_sig?: string;
  /**
   * Codex r3 #6: what a refused send carried besides its text (files, the addressed agent, cards),
   * put back with it, so sending the draft again sends them too (`draftExtras`).
   */
  extras?: DraftExtras;
};

/** The parts of a send that live outside the editor's blocks. */
export type DraftExtras = {
  /**
   * The send's files as they were shown (chat file views): put back on the composer's strip when
   * it opens (`takeDraftFiles`), where they can be seen and removed (Codex r4 #9).
   */
  files?: unknown[];
  /** Ids of files with no view kept (an older entry): sent with the draft as they were. */
  attachment_ids?: string[];
  addressee?: string;
  card_refs?: unknown[];
};

function extrasOf(
  request: Record<string, unknown> | undefined,
  files: unknown[] | undefined,
): DraftExtras | undefined {
  if (!request) return undefined;
  const out: DraftExtras = {};
  if (files?.length) out.files = files;
  else if (Array.isArray(request.attachment_ids) && request.attachment_ids.length)
    out.attachment_ids = request.attachment_ids.map(String);
  if (typeof request.addressee === "string") out.addressee = request.addressee;
  if (Array.isArray(request.card_refs) && request.card_refs.length)
    out.card_refs = request.card_refs;
  return Object.keys(out).length ? out : undefined;
}

/** The extras a draft was put back with (none: a plain draft). */
export function draftExtras(key: string | null | undefined): DraftExtras | null {
  return key ? (readRecord(key)?.extras ?? null) : null;
}

/**
 * The files and addressee a draft was put back with, for the composer to show (its strip and its
 * addressee chip), where they are visible, removable, and sent from. Codex r5 #3: read, not taken —
 * the draft keeps them until it is sent or discarded, so leaving and reopening the conversation
 * shows them again; removing one in the composer drops it from the draft (`forgetDraftShown`).
 */
export function draftShown(key: string): { files: unknown[]; addressee: string | null } {
  const extras = readRecord(key)?.extras;
  return { files: extras?.files ?? [], addressee: extras?.addressee ?? null };
}

/**
 * Drops a removed file (by id) from the draft at `key`, or records the addressee picked in place
 * of the recovered one (Codex r6 #1: "Nobody" included), so reopening shows the pick, not the
 * old one or the server's default.
 */
export function forgetDraftShown(key: string, what: { file?: string; addressee?: string }): void {
  try {
    const s = storage();
    const d = readRecord(key);
    if (!s || !d?.extras) return;
    const extras: DraftExtras = { ...d.extras };
    if (what.file && extras.files) {
      extras.files = extras.files.filter((f) => (f as { id?: unknown }).id !== what.file);
      if (!extras.files.length) delete extras.files;
    }
    if (what.addressee !== undefined) extras.addressee = what.addressee;
    const next: StoredDraft = { ...d, extras };
    if (!Object.keys(extras).length) delete next.extras;
    s.setItem(key, JSON.stringify(next));
  } catch {}
}

/**
 * Keys sorted recursively, with a block's own `id` dropped (the editor re-makes it on reload): what
 * a block says, not how the editor keyed it. Codex r5 #4: only blocks' ids (the body's items and
 * their `children`) — every other `id`, a mention's `props.id` among them, is content and kept.
 */
function canonical(v: unknown, block = false): unknown {
  if (Array.isArray(v)) return v.map((x) => canonical(x, block));
  if (v === null || typeof v !== "object") return v;
  const out: Record<string, unknown> = {};
  for (const k of Object.keys(v as Record<string, unknown>).sort()) {
    if (block && k === "id") continue;
    out[k] = canonical((v as Record<string, unknown>)[k], block && k === "children");
  }
  return out;
}

/** A body's blocks in canonical form (links keep their label beside their target). */
export function canonicalBlocks(blocks: readonly unknown[]): unknown {
  return canonical(blocks, true);
}

/**
 * CHAT-SWITCH-B: what makes two sends the same message — where it goes and everything it says or
 * carries (words, link targets, mentions, broadcast, reply root, files, addressee, cards), not
 * the editor's block shapes. A draft keeps its old idempotency key only for an equal request.
 */
export function requestSig(threadId: string, request: Record<string, unknown>): string {
  // Codex r2 #9 … r4 #7: the body itself, canonical — every block's type and settings, each
  // text run with its exact words (whitespace kept) and styles, each link with its own label —
  // with only what the editor re-makes on reload (block ids, key order) left out.
  const body = Array.isArray(request.body)
    ? canonicalBlocks(request.body)
    : { markdown: String(request.body_markdown ?? "") };
  const sorted = (v: unknown) => (Array.isArray(v) ? [...v].map(String).sort() : []);
  return JSON.stringify({
    thread: threadId,
    body,
    mentions: sorted(request.mentions),
    broadcast: request.broadcast ?? null,
    parent: request.parent_message_id ?? null,
    files: sorted(request.attachment_ids),
    addressee: request.addressee ?? null,
    cards: request.card_refs ?? null,
  });
}

/** The storage key of a draft: one per company, person and place (thread, topic, DM). */
export function draftKey(co: string, meId: string, place: string): string {
  return `${DRAFT_PREFIX}:${co}:${meId}:${place}`;
}

function storage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

/** The saved draft at `key` (null: none, expired, unreadable). */
export function readDraft(key: string, now = Date.now()): unknown[] | null {
  try {
    const raw = storage()?.getItem(key);
    if (!raw) return null;
    const d = JSON.parse(raw) as StoredDraft;
    if (!Array.isArray(d.blocks) || typeof d.at !== "number" || now - d.at > DRAFT_TTL_MS) {
      storage()?.removeItem(key);
      return null;
    }
    return d.blocks;
  } catch {
    return null;
  }
}

function readRecord(key: string): StoredDraft | null {
  try {
    const raw = storage()?.getItem(key);
    if (!raw) return null;
    const d = JSON.parse(raw) as StoredDraft;
    return Array.isArray(d.blocks) && typeof d.at === "number" ? d : null;
  } catch {
    return null;
  }
}

/**
 * Saves (or, with null, removes) the draft at `key`; a full or blocked storage keeps none. A draft
 * brought back from an unacknowledged send keeps that send's idempotency key (`sentAs`, or the one
 * it already has) while it is edited: whether to reuse it is decided at Send, by its text.
 */
export function writeDraft(
  key: string,
  blocks: unknown[] | null,
  now = Date.now(),
  sentAs?: { clientId: string; sig: string; extras?: DraftExtras | undefined },
): void {
  try {
    const s = storage();
    if (!s) return;
    if (blocks === null) {
      s.removeItem(key);
      return;
    }
    const prev = sentAs ? null : readRecord(key);
    const keyed = sentAs
      ? {
          client_id: sentAs.clientId,
          client_sig: sentAs.sig,
          ...(sentAs.extras ? { extras: sentAs.extras } : {}),
        }
      : prev?.client_id
        ? {
            client_id: prev.client_id,
            client_sig: prev.client_sig ?? "",
            ...(prev.extras ? { extras: prev.extras } : {}),
          }
        : {};
    s.setItem(key, JSON.stringify({ blocks, at: now, ...keyed } satisfies StoredDraft));
  } catch {
    // Quota or privacy mode: the draft lives as long as the composer does.
  }
}

/**
 * CHAT-SWITCH-B: the idempotency key a send from the draft at `key` reuses — the key of the send it
 * was brought back from, while the request is the same one (`requestSig`) — else null (fresh).
 */
export function draftClientId(key: string | null | undefined, sig: string): string | null {
  if (!key) return null;
  const d = readRecord(key);
  return d?.client_id && d.client_sig === sig ? d.client_id : null;
}

// A sent message until the server has it (`chat-optimistic` `rescueHeld`): one entry per send, by
// its client id, with the whole request (CHAT-SWITCH-B), so a reload mid-send loses none of them
// and none posts twice: the next chat load sends each again under its own client id
// (`reconcileUnsent`), and the server answers the first message if it had it.
const unsentPrefix = (key: string) => `${key}:unsent:`;

type Unsent = {
  blocks: unknown[];
  topic: string | null;
  at: number;
  /** CHAT-SWITCH-B: the request as sent (its `client_id` is the idempotency key) and where. */
  request?: Record<string, unknown> & { client_id: string };
  threadId?: string;
  /** The request's `requestSig` (what a draft brought back from it is compared with). */
  sig?: string;
  /** The send's file views (what its strip showed), to show again if it comes back. */
  files?: unknown[];
  /** It failed in this browser: back to its draft, never sent again on its own. */
  failed?: boolean;
};

/** Sends still running in this tab: theirs are not taken back into a composer. */
const sending = new Set<string>();

/**
 * A failed send. Refused (`definite`, a 4xx the same request would meet again): free to go back
 * into its draft, never resent on its own. Not known to have reached the server (offline, a lost
 * answer, busy, a server error; Codex r2 #4): its whole request — files, addressee and all — stays
 * as it is, under its client id, and the next load (or coming back online) sends it again, so the
 * server answers the first message if it had it and nothing is lost or posted twice.
 */
export function releaseUnsent(clientId: string, key?: string, definite = true): void {
  sending.delete(clientId);
  if (!key || !definite) return;
  try {
    const s = storage();
    const k = `${unsentPrefix(key)}${clientId}`;
    const u = JSON.parse(s?.getItem(k) ?? "null") as Unsent | null;
    if (u) s?.setItem(k, JSON.stringify({ ...u, failed: true } satisfies Unsent));
  } catch {}
}

export function holdUnsent(
  key: string,
  clientId: string,
  blocks: unknown[],
  topic: string | null,
  now = Date.now(),
  sent?: { request: NonNullable<Unsent["request"]>; threadId: string; files?: unknown[] },
): void {
  sending.add(clientId);
  const row: Unsent = { blocks, topic, at: now };
  if (sent) {
    const { files, ...rest } = sent;
    Object.assign(row, rest, { sig: requestSig(sent.threadId, sent.request) });
    if (files?.length) row.files = files;
  }
  try {
    storage()?.setItem(`${unsentPrefix(key)}${clientId}`, JSON.stringify(row));
  } catch {
    // As for drafts.
  }
}

export function dropUnsent(key: string, clientId: string): void {
  sending.delete(clientId);
  try {
    storage()?.removeItem(`${unsentPrefix(key)}${clientId}`);
  } catch {}
}

/** An unacknowledged send the next load sends again by itself (a reload mid-send). */
const pendingResend = (u: Unsent) => !u.failed && u.request !== undefined && !!u.threadId;

/**
 * The oldest text sent from `key` that the server never acknowledged and that is not being sent
 * again (a failure while other text was typed, a send from before the request was kept), taken
 * out of the store; null: none.
 */
export function takeUnsent(key: string, now = Date.now()): (Unsent & { clientId: string }) | null {
  try {
    const s = storage();
    if (!s) return null;
    const prefix = unsentPrefix(key);
    let best: { k: string; u: Unsent } | null = null;
    for (let i = 0; i < s.length; i++) {
      const k = s.key(i);
      if (!k?.startsWith(prefix) || sending.has(k.slice(prefix.length))) continue;
      const u = JSON.parse(s.getItem(k) ?? "null") as Unsent | null;
      if (!u || !Array.isArray(u.blocks) || typeof u.at !== "number") continue;
      if (now - u.at > DRAFT_TTL_MS || pendingResend(u)) continue;
      if (!best || u.at < best.u.at) best = { k, u };
    }
    if (!best) return null;
    s.removeItem(best.k);
    return { ...best.u, clientId: best.k.slice(prefix.length) };
  } catch {
    return null;
  }
}

/** A draft put back while its composer may be open: `useDraft` reopens an empty one on it. */
const RESTORED_EVENT = "cos:chat-draft-restored";

/**
 * HELPERS ("Ask the DE"): adds lines to the draft at `key` and tells an open composer of that
 * place to reopen on it (one being typed in keeps its own text).
 */
export function appendToDraft(key: string, blocks: unknown[]): void {
  writeDraft(key, [...(readDraft(key) ?? []), ...blocks]);
  if (typeof window !== "undefined")
    window.dispatchEvent(new CustomEvent(RESTORED_EVENT, { detail: key }));
}

/** Puts an unacknowledged send back in its draft, keyed by its client id (see `writeDraft`). */
function restore(key: string, u: Unsent & { clientId: string }, announce = false): void {
  writeDraft(
    key,
    u.blocks,
    Date.now(),
    u.request && u.sig
      ? { clientId: u.clientId, sig: u.sig, extras: extrasOf(u.request, u.files) }
      : undefined,
  );
  if (u.topic) writeDraftTopic(key, u.topic);
  if (announce && typeof window !== "undefined")
    window.dispatchEvent(new CustomEvent(RESTORED_EVENT, { detail: key }));
}

/** A refusal that sending the same request again would meet too (not offline, busy or late). */
export const refused = (e: unknown): boolean => {
  const status = (e as { status?: unknown })?.status;
  return (
    typeof status === "number" && status >= 400 && status < 500 && status !== 408 && status !== 429
  );
};

/** The draft to open `key` with: the saved one, else a send the server never acknowledged. */
function openingDraft(key: string): unknown[] | null {
  const saved = readDraft(key);
  if (saved) return saved;
  const unsent = takeUnsent(key);
  if (!unsent) return null;
  restore(key, unsent);
  return unsent.blocks;
}

/** Companies (and people) whose unacknowledged sends this tab has already sent again. */
const reconciled = new Set<string>();

/**
 * CHAT-SWITCH-B: once per load, every send of this person in this company that a reload (or a
 * closed tab) cut off before the server answered is sent again with its own request — the same
 * `client_id`, so a message the server already has is answered, not posted again. Each one the
 * server has (now or before) leaves the store and its draft. One that could not reach the server
 * (offline, busy, a server error) keeps its whole request — files, addressee and all — and is
 * sent again on the next try (`useReconcileUnsent`: back online, or the next load). One the server
 * refused goes back into its draft, still keyed, so sending that same request again posts it at
 * most once. Returns what was sent, put back and still waiting.
 */
export async function reconcileUnsent(
  co: string,
  meId: string,
  post: (threadId: string, request: Record<string, unknown>) => Promise<unknown>,
  now = Date.now(),
): Promise<{ sent: string[]; restored: number; waiting: number }> {
  const none = { sent: [], restored: 0, waiting: 0 };
  const done = `${co}:${meId}`;
  if (reconciled.has(done)) return none;
  reconciled.add(done);
  const s = storage();
  if (!s) return none;
  const prefix = `${DRAFT_PREFIX}:${co}:${meId}:`;
  const found: { k: string; draft: string; clientId: string; u: Unsent }[] = [];
  try {
    for (let i = 0; i < s.length; i++) {
      const k = s.key(i);
      const at = k?.indexOf(":unsent:") ?? -1;
      if (!k?.startsWith(prefix) || at < 0) continue;
      const clientId = k.slice(at + ":unsent:".length);
      if (sending.has(clientId)) continue;
      const u = JSON.parse(s.getItem(k) ?? "null") as Unsent | null;
      if (!u || now - u.at > DRAFT_TTL_MS || !pendingResend(u)) continue;
      found.push({ k, draft: k.slice(0, at), clientId, u });
    }
  } catch {
    return none;
  }
  const sent: string[] = [];
  let restored = 0;
  let waiting = 0;
  for (const f of found) {
    sending.add(f.clientId);
    try {
      await post(f.u.threadId as string, f.u.request as Record<string, unknown>);
      sent.push(f.u.threadId as string);
      dropUnsent(f.draft, f.clientId);
      const d = readRecord(f.draft);
      if (d?.client_id === f.clientId) writeDraft(f.draft, null);
    } catch (e) {
      if (!refused(e)) {
        // Not reached: the whole request stays as it is, for the next try.
        sending.delete(f.clientId);
        waiting += 1;
        continue;
      }
      // Refused. Never over newer typing: with a draft already there, the text waits (marked
      // failed) for its composer to open empty; else it goes back now, and an open composer
      // that is empty shows it.
      if (readDraft(f.draft)) releaseUnsent(f.clientId, f.draft);
      else {
        dropUnsent(f.draft, f.clientId);
        restore(f.draft, { ...f.u, clientId: f.clientId }, true);
      }
      restored += 1;
    }
  }
  return { sent, restored, waiting };
}

/** Tests: forget which companies were reconciled this load. */
export function resetReconciled(): void {
  reconciled.clear();
}

/**
 * Runs `reconcileUnsent` once the chat knows who the viewer is (and again when the browser is
 * back online, for sends still waiting), and refreshes what it sent.
 */
export function useReconcileUnsent(chat: Pick<ChatContext, "api" | "co" | "meId" | "readOnly">) {
  const queryClient = useQueryClient();
  const { api, co, meId, readOnly } = chat;
  useEffect(() => {
    if (!meId || readOnly) return;
    let told = false;
    // Busy or a server error while still online: a few more tries, further apart (Codex r2 #10).
    let tries = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const later = () => {
      if (timer || tries >= 5) return;
      tries += 1;
      timer = setTimeout(() => {
        timer = null;
        again();
      }, 15_000 * tries);
    };
    const run = () =>
      void reconcileUnsent(co, meId, (threadId, request) =>
        api.client.messages.create({
          params: { co, id: threadId },
          body: request as Parameters<typeof api.client.messages.create>[0]["body"],
        }),
      ).then(({ sent, restored, waiting }) => {
        for (const t of new Set(sent))
          void queryClient.invalidateQueries({ queryKey: chatKeys.messages(co, t) });
        if (restored)
          toast.add({
            title: "A message from before the reload was not sent: it is back in its draft.",
            type: "error",
          });
        if (waiting) later();
        if (waiting && !told) {
          told = true;
          toast.add({
            title:
              "A message from before the reload is waiting to send. It goes as soon as it can.",
            type: "info",
          });
        }
      });
    function again() {
      reconciled.delete(`${co}:${meId}`);
      run();
    }
    run();
    window.addEventListener("online", again);
    return () => {
      window.removeEventListener("online", again);
      if (timer) clearTimeout(timer);
    };
  }, [api, co, meId, readOnly, queryClient]);
}

/**
 * CHAT-SWITCH-B (Codex r4 #9): when a composer opens (or reopens) on a draft put back from a
 * refused send, its files go back on the strip and its addressee on the chip (`draftShown`).
 * `mount` changes when the draft is put back while the composer is open. The draft keeps them
 * (Codex r5 #3): the strip never discards them on leaving, and a ✕ or another addressee drops
 * them from the draft too.
 */
export function useRecoveredDraft(
  mount: string | null,
  key: string | null,
  staged: {
    putBack: (files: readonly ChatFileView[], kept?: { onRemove: (id: string) => void }) => void;
  },
  addressee?: { setPick: (p: never) => void; pick?: unknown } | undefined,
): void {
  const { putBack } = staged;
  const setPick = addressee?.setPick;
  const pick = addressee?.pick;
  const shownPick = useRef<string | null>(null);
  useEffect(() => {
    if (!mount || !key) return;
    const shown = draftShown(key);
    if (shown.files.length)
      putBack(shown.files as ChatFileView[], {
        onRemove: (id) => forgetDraftShown(key, { file: id }),
      });
    shownPick.current = shown.addressee && setPick ? shown.addressee : null;
    if (shown.addressee && setPick) (setPick as (p: string) => void)(shown.addressee);
  }, [mount, key, putBack, setPick]);
  // The chip moved off the recovered addressee: the draft carries the new pick instead.
  useEffect(() => {
    // (null: not picked yet — the recovered pick lands on the next render — or just sent.)
    if (!key || shownPick.current === null || typeof pick !== "string") return;
    if (pick === shownPick.current) return;
    forgetDraftShown(key, { addressee: pick });
    shownPick.current = pick;
  }, [key, pick]);
}

type ChatFileView = MessageView["attachments"][number];

/** The topic field typed beside a stream draft (so a reload sends it where it was meant to go). */
export function readDraftTopic(key: string, now = Date.now()): string | null {
  try {
    const raw = storage()?.getItem(`${key}:topic`);
    if (!raw) return null;
    const d = JSON.parse(raw) as { topic?: unknown; at?: unknown };
    if (typeof d.topic !== "string" || typeof d.at !== "number" || now - d.at > DRAFT_TTL_MS)
      return null;
    return d.topic;
  } catch {
    return null;
  }
}

/** Saves (or, with null or "", removes) the topic typed beside the draft at `key`. */
export function writeDraftTopic(key: string, topic: string | null, now = Date.now()): void {
  try {
    const s = storage();
    if (!s) return;
    if (!topic) s.removeItem(`${key}:topic`);
    else s.setItem(`${key}:topic`, JSON.stringify({ topic, at: now }));
  } catch {
    // As for the draft itself.
  }
}

/**
 * The Composer's `draft` for `place` (a thread or topic id, or the channel stream), with the key
 * to remount it by. None for a reader without a membership (agency staff never post).
 */
export function useDraft(
  chat: Pick<ChatContext, "co" | "meId">,
  place: string | null,
): { key: string; mount: string; draft: NonNullable<ComposerProps["draft"]> } | null {
  const key = chat.meId && place ? draftKey(chat.co, chat.meId, place) : null;
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pending = useRef<{ key: string; blocks: unknown[] | null } | null>(null);
  // CHAT-SWITCH-B: a refused send put back while this composer is open (`reconcileUnsent`
  // finishes after it mounted): an empty composer reopens on it (`mount` is its React key).
  const [restored, setRestored] = useState(0);
  useEffect(() => {
    if (!key) return;
    const on = (e: Event) => {
      if ((e as CustomEvent<string>).detail !== key) return;
      if (pending.current?.blocks) return; // typing not yet saved: never written over
      setRestored((n) => n + 1);
    };
    window.addEventListener(RESTORED_EVENT, on);
    return () => window.removeEventListener(RESTORED_EVENT, on);
  }, [key]);
  const flush = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    const p = pending.current;
    pending.current = null;
    if (!p) return;
    writeDraft(p.key, p.blocks);
    // Sent or emptied: the topic typed beside it goes too.
    if (p.blocks === null) writeDraftTopic(p.key, null);
  }, []);
  // Leaving the place (or the page) writes what is pending at once.
  useEffect(() => {
    if (!key) return;
    const onHide = () => flush();
    window.addEventListener("pagehide", onHide);
    return () => {
      window.removeEventListener("pagehide", onHide);
      flush();
    };
  }, [key, flush]);
  return useMemo(() => {
    if (!key) return null;
    void restored;
    return {
      key,
      mount: `${key}#${restored}`,
      draft: {
        initial: openingDraft(key),
        save: (blocks: unknown[] | null) => {
          pending.current = { key, blocks };
          // Cleared (sent, emptied): at once, so a quick reload never brings it back.
          if (blocks === null) return flush();
          if (timer.current) clearTimeout(timer.current);
          timer.current = setTimeout(flush, 400);
        },
      },
    };
  }, [key, flush, restored]);
}

// @channel / @here -------------------------------------------------------------------------------

const isDmKind = (kind: string) => kind === "dm" || kind === "group_dm";

/**
 * The @ menu of a channel's composer: `@channel` and `@here` first (Slack's broadcast), then the
 * people and DEs. Not in a DM; marked "Admins only" (and off) when the channel keeps them to its
 * managers and the viewer is not one.
 */
export function useBroadcastMentionables(
  chat: ChatContext,
  channel: Pick<Channel, "id" | "kind" | "broadcast_policy" | "archived_at"> | undefined,
  base: MentionItem[],
): MentionItem[] {
  const usable = channel !== undefined && !isDmKind(channel.kind) && !channel.archived_at;
  const manages = useCanManage(chat, channel ?? { id: "" });
  return useMemo(() => {
    if (!usable || !channel) return base;
    const locked = channel.broadcast_policy === "admins" && !manages;
    const item = (kind: "channel" | "here", hint: string): MentionItem => ({
      id: BROADCAST_REF[kind],
      name: kind,
      kind: "person",
      group: "Notify",
      hint,
      ...(locked ? { disabled: true, badge: "Admins only" } : {}),
    });
    return [
      item("channel", "Everyone in this channel"),
      item("here", "Everyone active now"),
      ...base,
    ];
  }, [usable, channel, manages, base]);
}

// Reminders --------------------------------------------------------------------------------------

export type RemindPreset = "20m" | "1h" | "3h" | "tomorrow" | "next-week";

export const REMIND_PRESETS: { id: RemindPreset; label: string }[] = [
  { id: "20m", label: "In 20 minutes" },
  { id: "1h", label: "In 1 hour" },
  { id: "3h", label: "In 3 hours" },
  { id: "tomorrow", label: "Tomorrow at 9:00" },
  { id: "next-week", label: "Next Monday at 9:00" },
];

/** When a preset fires, from `now` (local time for the day-based ones). */
export function remindAt(preset: RemindPreset, now = new Date()): Date {
  const at = new Date(now);
  switch (preset) {
    case "20m":
      return new Date(now.getTime() + 20 * 60_000);
    case "1h":
      return new Date(now.getTime() + 60 * 60_000);
    case "3h":
      return new Date(now.getTime() + 3 * 60 * 60_000);
    case "tomorrow":
      at.setDate(at.getDate() + 1);
      at.setHours(9, 0, 0, 0);
      return at;
    case "next-week": {
      // The next Monday (a week on, when today is Monday).
      const days = (1 - at.getDay() + 7) % 7 || 7;
      at.setDate(at.getDate() + days);
      at.setHours(9, 0, 0, 0);
      return at;
    }
  }
}

const UNIT_MS: Record<string, number> = { m: 60_000, h: 3_600_000, d: 86_400_000 };

/**
 * A typed `/remind …` (Slack's): `/remind [me] [to] <what> in 20 minutes | in 2h | tomorrow |
 * at 15:30`, the time part first or last. Null when it names no time (the dialog then asks).
 */
export function parseRemind(input: string, now = new Date()): { text: string; at: Date } | null {
  let rest = input
    .trim()
    .replace(/^\/remind\b/i, "")
    .trim()
    .replace(/^me\b/i, "")
    .trim();
  let at: Date | null = null;
  const take = (re: RegExp, f: (m: RegExpExecArray) => Date | null) => {
    if (at) return;
    const m = re.exec(rest);
    if (!m) return;
    const d = f(m);
    if (!d) return;
    at = d;
    rest = `${rest.slice(0, m.index)} ${rest.slice(m.index + m[0].length)}`.trim();
  };
  take(/\bin\s+(\d{1,3})\s*(m|min|mins|minutes?|h|hrs?|hours?|d|days?)\b/i, (m) => {
    const unit = (m[2] ?? "m").toLowerCase()[0] ?? "m";
    return new Date(now.getTime() + Number(m[1]) * (UNIT_MS[unit] ?? 60_000));
  });
  take(/\btomorrow\b/i, () => remindAt("tomorrow", now));
  take(/\bat\s+([01]?\d|2[0-3]):([0-5]\d)\b/i, (m) => {
    const d = new Date(now);
    d.setHours(Number(m[1]), Number(m[2]), 0, 0);
    if (d.getTime() <= now.getTime()) d.setDate(d.getDate() + 1);
    return d;
  });
  if (!at) return null;
  const text = rest.replace(/^to\b/i, "").trim().replace(/\s+/g, " ");
  return text ? { text, at } : null;
}

export const timeLabel = (d: Date) =>
  d.toLocaleString(undefined, { weekday: "short", hour: "2-digit", minute: "2-digit" });

export const remindersKey = (co: string) => ["company", co, "chat", "reminders"] as const;

export const remindersQuery = (chat: Pick<ChatContext, "api" | "co">) => ({
  queryKey: remindersKey(chat.co),
  queryFn: () => chat.api.client.chatReminders.list({ params: { co: chat.co } }),
});

type Target = { threadId: string; messageId: string } | null;

function useCreateReminder(chat: ChatContext) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (v: { at: Date; target: Target; text?: string | undefined }) =>
      chat.api.client.chatReminders.create({
        params: { co: chat.co },
        body: {
          at: v.at.toISOString(),
          ...(v.text ? { text: v.text } : {}),
          ...(v.target ? { thread_id: v.target.threadId, message_id: v.target.messageId } : {}),
        },
      }),
    onSuccess: (r) => {
      toast.add({ title: `I'll remind you ${timeLabel(new Date(r.at))}`, type: "success" });
      void queryClient.invalidateQueries({ queryKey: remindersKey(chat.co) });
    },
    onError: (e) =>
      toast.add({
        title: "Could not set the reminder",
        description: problemText(e, e instanceof Error ? e.message : String(e)),
        type: "error",
      }),
  });
}

/** "Remind me" for a custom time, or `/remind` (with its text). */
function RemindDialog({
  chat,
  target,
  initialText,
  onClose,
}: {
  chat: ChatContext;
  target: Target;
  initialText?: string;
  /** `created`: a reminder was set (false: cancelled). */
  onClose: (created: boolean) => void;
}) {
  const create = useCreateReminder(chat);
  const [text, setText] = useState(initialText ?? "");
  const local = (d: Date) => {
    const p = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
  };
  const [when, setWhen] = useState(() => local(remindAt("1h")));
  const at = when ? new Date(when) : null;
  const valid = at !== null && !Number.isNaN(at.getTime()) && at.getTime() > Date.now();
  const needsText = target === null;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!valid || !at || (needsText && !text.trim())) return;
    create.mutate(
      { at, target, text: needsText ? text.trim() : undefined },
      { onSuccess: () => onClose(true) },
    );
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !create.isPending && onClose(false)}>
      <DialogContent>
        <form onSubmit={submit} className="flex flex-col gap-5">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {target ? "Remind me about this" : "Set a reminder"}
              <InfoTip label="About reminders">
                It comes to your inbox at that time. Only you see it.
              </InfoTip>
            </DialogTitle>
          </DialogHeader>
          {needsText ? (
            <Field>
              <FieldLabel htmlFor="remind-text">Remind me to</FieldLabel>
              <Input
                id="remind-text"
                className="h-10"
                maxLength={500}
                value={text}
                onChange={(e) => setText(e.target.value)}
                autoFocus
              />
            </Field>
          ) : null}
          <Field>
            <FieldLabel htmlFor="remind-when">When</FieldLabel>
            <Input
              id="remind-when"
              type="datetime-local"
              className="h-10"
              value={when}
              onChange={(e) => setWhen(e.target.value)}
            />
          </Field>
          <fieldset className="m-0 flex flex-wrap gap-1.5 border-0 p-0" aria-label="Quick times">
            {REMIND_PRESETS.map((p) => (
              <Button
                key={p.id}
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setWhen(local(remindAt(p.id)))}
              >
                {p.label}
              </Button>
            ))}
          </fieldset>
          <DialogFooter>
            <DialogClose render={<Button variant="ghost" type="button" />}>Cancel</DialogClose>
            <Button
              type="submit"
              disabled={!valid || (needsText && !text.trim()) || create.isPending}
            >
              Set reminder
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Reminders for one conversation: the message menu's "Remind me" (presets and Custom…), the
 * `/remind` command, a typed `/remind …` caught before it is posted, and the dialog to render.
 */
export function useReminders(chat: ChatContext) {
  const create = useCreateReminder(chat);
  const [dialog, setDialog] = useState<{
    target: Target;
    text?: string;
    /** `/remind` without a time: tells the waiting composer whether to clear. */
    settle?: (created: boolean) => void;
  } | null>(null);
  const usable = chat.meId !== null && !chat.readOnly;
  const menuItems = (row: MessageView) =>
    usable && !row.deleted_at && !row.id.startsWith("local-") ? (
      <DropdownMenuSub>
        <DropdownMenuSubTrigger>
          <AlarmClockIcon />
          Remind me
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent className="min-w-44">
          {REMIND_PRESETS.slice(0, 4).map((p) => (
            <DropdownMenuItem
              key={p.id}
              onClick={() =>
                create.mutate({
                  at: remindAt(p.id),
                  target: { threadId: row.thread_id, messageId: row.id },
                })
              }
            >
              {p.label}
            </DropdownMenuItem>
          ))}
          <DropdownMenuItem
            onClick={() => setDialog({ target: { threadId: row.thread_id, messageId: row.id } })}
          >
            Custom…
          </DropdownMenuItem>
        </DropdownMenuSubContent>
      </DropdownMenuSub>
    ) : null;
  const command: SlashCommand | null = usable
    ? {
        id: "remind",
        title: "Remind",
        description: "Set a reminder for yourself",
        aliases: ["reminder"],
        icon: <AlarmClockIcon className="size-4" />,
        onRun: () => setDialog({ target: null }),
      }
    : null;
  /** A sent `/remind …`: set it (a time was named) or open the dialog with the text; else false. */
  const intercept = async (value: ComposerValue): Promise<boolean> => {
    const typed = value.text.trim();
    if (!usable || !/^\/remind\b/i.test(typed)) return false;
    const parsed = parseRemind(typed);
    if (!parsed) {
      const text = typed
        .replace(/^\/remind\b/i, "")
        .trim()
        .replace(/^me\b/i, "")
        .trim()
        .replace(/^to\b/i, "")
        .trim();
      // No time yet: ask for one. The composer keeps the text until a reminder is set (a
      // cancelled dialog leaves it, and its draft, as typed).
      return new Promise<boolean>((resolve, reject) =>
        setDialog({
          target: null,
          text,
          settle: (created) => (created ? resolve(true) : reject(new Error("cancelled"))),
        }),
      );
    }
    await create.mutateAsync({ at: parsed.at, target: null, text: parsed.text });
    return true;
  };
  const node = dialog ? (
    <RemindDialog
      chat={chat}
      target={dialog.target}
      {...(dialog.text !== undefined ? { initialText: dialog.text } : {})}
      onClose={(created) => {
        dialog.settle?.(created);
        setDialog(null);
      }}
    />
  ) : null;
  return { menuItems, command, intercept, dialog: node };
}

/** Saved › Reminders: the open ones soonest first (and those fired today), each with Cancel. */
export function RemindersSection({ chat }: { chat: ChatContext }) {
  const queryClient = useQueryClient();
  const list = useQuery({ ...remindersQuery(chat), enabled: chat.meId !== null });
  const cancel = useMutation({
    mutationFn: (id: string) =>
      chat.api.client.chatReminders.dismiss({ params: { co: chat.co, id } }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: remindersKey(chat.co) }),
  });
  if (chat.meId === null) return null;
  const rows: ChatReminder[] = list.data?.data ?? [];
  return (
    <section aria-label="Reminders" data-section="reminders" className="mb-4 flex flex-col gap-1">
      <h2 className="flex items-center gap-1.5 px-2 text-xs font-medium text-muted-foreground uppercase">
        Reminders
        <InfoTip label="About reminders">
          Set from a message's menu or with /remind. They come to your inbox.
        </InfoTip>
      </h2>
      {list.isSuccess && !rows.length ? (
        <EmptyState
          variant="compact"
          icon={BellRingIcon}
          title="No reminders"
          description="Use “Remind me” on a message, or type /remind."
        />
      ) : null}
      <ul className="flex flex-col">
        {rows.map((r) => {
          const at = new Date(r.at);
          const due = r.fired_at !== null;
          const title = (
            <span className="truncate text-sm text-foreground">{r.text || "(no text)"}</span>
          );
          return (
            <li
              key={r.id}
              data-reminder={r.id}
              className="group/reminder flex items-center gap-3 rounded-md px-2 py-1.5 hover:bg-muted"
            >
              <AlarmClockIcon
                aria-hidden={true}
                className={`size-4 shrink-0 ${due ? "text-warning-text" : "text-primary-text"}`}
              />
              <div className="flex min-w-0 flex-1 flex-col">
                {r.channel_id && r.topic_id ? (
                  <Link
                    to="/c/$slug/chat/$channelId/$topicId"
                    params={{
                      slug: chat.company.slug,
                      channelId: r.channel_id,
                      topicId: r.topic_id,
                    }}
                    search={(s: Record<string, unknown>) =>
                      ({
                        ...keepFocus(s),
                        thread: r.parent_message_id ?? r.message_id ?? undefined,
                      }) as never
                    }
                    className="truncate hover:underline"
                  >
                    {title}
                  </Link>
                ) : (
                  title
                )}
                <span className="text-xs text-muted-foreground">
                  {due ? "Reminded " : ""}
                  {timeLabel(at)}
                </span>
              </div>
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label={due ? "Clear reminder" : "Cancel reminder"}
                title={due ? "Clear" : "Cancel"}
                className="opacity-0 group-hover/reminder:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100"
                disabled={cancel.isPending}
                onClick={() => cancel.mutate(r.id)}
              >
                <XIcon />
              </Button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
